import React, { useState, useRef } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Upload, FileText, AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useCreatePagamentoEvidencia } from '@/modules/financiamento/hooks/useNetFaturamentos';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

interface EvidenciaUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  faturamentoId: string;
  contratoId: string;
  boletimId: string;
}

const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_SIZE_MB = 10;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

function humanizeReason(reason?: string): string {
  switch (reason) {
    case 'file_too_large':
      return 'Arquivo excede o limite de 10MB.';
    case 'extension_not_allowed':
      return 'Tipo de arquivo não permitido. Use PDF, JPG, PNG ou WebP.';
    case 'mime_extension_mismatch':
      return 'Arquivo rejeitado: a extensão não corresponde ao tipo declarado.';
    case 'content_mismatch':
      return 'Arquivo rejeitado: o conteúdo não corresponde à extensão informada.';
    case 'empty_file':
      return 'Arquivo vazio.';
    default:
      return 'Arquivo rejeitado pela validação de segurança.';
  }
}

export function EvidenciaUploadDialog({
  open, onOpenChange, faturamentoId, contratoId, boletimId,
}: EvidenciaUploadDialogProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const createEvidencia = useCreatePagamentoEvidencia();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [valorComprovado, setValorComprovado] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setFile(null);
    setValorComprovado('');
    setObservacoes('');
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    setError(null);

    if (!selected) {
      setFile(null);
      return;
    }

    if (!ALLOWED_TYPES.includes(selected.type)) {
      setError('Tipo de arquivo não permitido. Use PDF, JPG, PNG ou WebP.');
      setFile(null);
      return;
    }

    if (selected.size > MAX_SIZE_BYTES) {
      setError(`Arquivo excede o limite de ${MAX_SIZE_MB}MB.`);
      setFile(null);
      return;
    }

    setFile(selected);
  };

  const handleUpload = async () => {
    if (!file || !user) return;
    setUploading(true);
    setError(null);

    try {
      // Build structured path: contrato/boletim/faturamento/evidencia_timestamp.ext
      const ext = file.name.split('.').pop()?.toLowerCase() || 'pdf';
      const timestamp = Date.now();
      const storagePath = `${contratoId}/${boletimId}/${faturamentoId}/evidencia_${timestamp}.${ext}`;

      // ---- Server-side validation (defense in depth) ---------------------
      // Magic bytes + MIME×extension consistency. Hard 5s client timeout.
      // On timeout / network error: fail-open, register validation_skipped.
      let validationSkipped = false;
      let validationReason: string | null = null;
      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('declaredMime', file.type);
        fd.append('declaredName', file.name);

        const ctrl = new AbortController();
        const timeoutId = setTimeout(() => ctrl.abort(), 5000);

        const { data: vData, error: vErr } = await supabase.functions.invoke(
          'validate-evidence-upload',
          { body: fd, signal: ctrl.signal as any },
        );
        clearTimeout(timeoutId);

        if (vErr) {
          validationSkipped = true;
          validationReason = vErr.message || 'invoke_error';
        } else if (vData && vData.valid === false) {
          // Real rejection — block upload and surface a clear message.
          const reasonMsg = humanizeReason(vData.reason);
          setError(reasonMsg);
          setUploading(false);
          return;
        }
      } catch (vCatch: any) {
        validationSkipped = true;
        validationReason = vCatch?.name === 'AbortError' ? 'timeout' : (vCatch?.message || 'unknown');
      }

      if (validationSkipped) {
        toast({
          title: 'Validação adicional não realizada',
          description: 'O upload prosseguiu com a validação básica.',
        });
      }
      // ---------------------------------------------------------------------

      const { error: uploadError } = await supabase.storage
        .from('payment-evidences')
        .upload(storagePath, file, { contentType: file.type });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('payment-evidences')
        .getPublicUrl(storagePath);

      const arquivo_url = urlData?.publicUrl || storagePath;

      await createEvidencia.mutateAsync({
        faturamento_id: faturamentoId,
        tipo: 'upload_comprovante',
        arquivo_url,
        valor_comprovado: valorComprovado ? Number(valorComprovado) : undefined,
        observacoes: observacoes.trim() || undefined,
      });

      // Audit trail — non-blocking
      try {
        await supabase.from('auditoria_eventos').insert({
          acao: 'evidence_upload',
          entidade: 'net_pagamentos_evidencias',
          entidade_id: faturamentoId,
          user_id: user.id,
          dados_resumo: {
            contrato_id: contratoId,
            boletim_id: boletimId,
            faturamento_id: faturamentoId,
            filename: file.name,
            mime: file.type,
            size: file.size,
            storage_path: storagePath,
            validation_skipped: validationSkipped,
            validation_reason: validationReason,
          },
        });
      } catch (auditErr) {
        console.warn('[EvidenciaUploadDialog] audit log failed', auditErr);
      }

      toast({ title: 'Evidência enviada com sucesso' });
      resetForm();
      onOpenChange(false);
    } catch (err: any) {
      setError(err.message || 'Erro ao enviar evidência');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetForm(); onOpenChange(v); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-primary" />
            Upload de Evidência de Pagamento
          </DialogTitle>
          <DialogDescription>
            Envie o comprovante de pagamento vinculado a este faturamento.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* File input */}
          <div className="space-y-2">
            <Label>Arquivo *</Label>
            <Input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp"
              onChange={handleFileChange}
              disabled={uploading}
            />
            <p className="text-xs text-muted-foreground">
              PDF ou imagem (JPG, PNG, WebP). Máximo {MAX_SIZE_MB}MB.
            </p>
          </div>

          {file && (
            <div className="flex items-center gap-2 text-xs bg-muted/50 rounded px-3 py-2">
              <FileText className="h-3.5 w-3.5 text-primary" />
              <span className="truncate">{file.name}</span>
              <span className="text-muted-foreground shrink-0">
                ({(file.size / 1024 / 1024).toFixed(2)}MB)
              </span>
            </div>
          )}

          {/* Valor comprovado */}
          <div className="space-y-2">
            <Label>Valor Comprovado (R$)</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={valorComprovado}
              onChange={(e) => setValorComprovado(e.target.value)}
              placeholder="0,00"
              disabled={uploading}
            />
            <p className="text-xs text-muted-foreground">Opcional. Valor efetivamente comprovado no documento.</p>
          </div>

          {/* Observações */}
          <div className="space-y-2">
            <Label>Observações</Label>
            <Textarea
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              placeholder="Observações adicionais..."
              rows={2}
              disabled={uploading}
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 rounded p-2">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={uploading}>
            Cancelar
          </Button>
          <Button onClick={handleUpload} disabled={!file || uploading}>
            {uploading ? 'Enviando...' : 'Enviar Evidência'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
